import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { readInventoryTransform, readInventoryEffectiveMeshScale } from './inventory-transform';
import { phpJsonFloat } from './php-decimal-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonTruthy } from './php-json-values';
import { createChargeReader } from './enchantment-inventory';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import type { ProviderCatalogRows } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { createInventoryReadContext } from './inventory-read-context';
import type { InventoryReadContext } from './inventory-read-context';
import { createLayerValueReader } from './layer-value-reader';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) throw new TypeError('Cannot write inventory computed totals to a scalar');
    return Object.fromEntries(Object.entries(value));
}
export class MissingInventoryEconomyInput extends Error {
    constructor(public readonly location: 'target' | 'source', public readonly column: string) {
        super(`Unavailable ${location} inventory economy column: ${column}`); this.name = 'MissingInventoryEconomyInput';
    }
}
function required(row: ItemFieldRow, key: string, location: 'target' | 'source'): unknown {
    if (!has(row, key)) throw new MissingInventoryEconomyInput(location, key);
    return own(row, key);
}
export interface InventoryFinalEconomyReaders {
    layerValue: (row: ItemFieldRow, name: string) => unknown;
    charges: (holder: unknown, item: unknown, fallback: unknown) => unknown;
    offHandId: string;
}
/** Contiguous native charges / intended slot / computed cost+weight stage.
 * Inputs are the intermediate inventory, the original quantity row and earlier
 * size ratio. This does not perform status, container, permission or picture work.
 */
export function compileInventoryFinalEconomy(target: ItemFieldRow, source: ItemFieldRow, sizeScale: number,
    readers: InventoryFinalEconomyReaders): ItemFieldRow {
    const out = writable(clone(target));
    const materialize = (key: string, value: unknown): void => {
        const layers = writable(out.item_layer_types); layers[key] = value; out.item_layer_types = layers;
    };
    if (phpJsonTruthy(readers.layerValue(out, 'item_charges'))) {
        const holder = required(out, 'inventory_id', 'target');
        const item = required(out, 'inventory_id', 'target');
        const fallback = Number(phpJsonInteger(readers.layerValue(out, 'item_charges')));
        materialize('current_item_charges', readers.charges(holder, item, fallback));
    }
    if (own(out.item_layer_types, 'equipment_slot') == null) {
        materialize('equipment_slot', readers.layerValue(out, 'equipment_slot') ?? readers.offHandId);
    }
    let cost = phpJsonFloat(readers.layerValue(out, 'cost_gp') ?? 0);
    const costScale = readInventoryTransform(out)?.scaleMode === 'absolute' ? readInventoryEffectiveMeshScale(out, sizeScale) : sizeScale;
    if (costScale !== 1) cost *= costScale ** 3;
    const axes = readInventoryTransform(out)?.scaleXYZ;
    if (Array.isArray(axes)) cost *= axes.reduce((total: number, axis: unknown) => total * phpJsonFloat(axis), 1);
    const weight = phpJsonFloat(readers.layerValue(out, 'weight') ?? 0);
    const quantity = phpJsonFloat(required(source, 'item_qty', 'source'));
    const enchantment = own(out.computed, 'enchantment');
    if (arrayLike(enchantment)) {
        cost += phpJsonFloat(own(enchantment, 'total_cost') ?? 0);
        const multipliers = own(enchantment, 'cost_multipliers') ?? [];
        // PHP warns and skips foreach on a scalar legacy value.
        for (const value of arrayLike(multipliers) ? Object.values(multipliers) : []) {
            const multiplier = phpJsonFloat(value); if (multiplier !== 0) cost *= multiplier;
        }
    }
    const override = readInventoryField(out, 'inventory_cost_gp');
    if (override !== null && override !== '') cost = phpJsonFloat(override);
    const computed = writable(out.computed), costs = writable(computed.cost);
    costs.total_gp = quantity * cost; computed.cost = costs;
    const weights = writable(computed.weight); weights.total = weight * quantity; computed.weight = weights;
    out.computed = computed;
    return clone(out) as ItemFieldRow;
}
export interface InventoryFinalEconomyDependencies {
    catalogRows: ProviderCatalogRows;
    offHandId: string;
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    resolveInventoryInstance: (id: string) => ItemFieldRow | null;
    /** Supply the request guard when composing stages; the context owns resolver tracing. */
    inventoryContext?: InventoryReadContext;
    chargePrefetch?: ItemFieldRow | null;
    fetchChargeColumn: (holder: unknown, item: unknown) => unknown;
    trace?: (event: readonly unknown[]) => void;
}
export function createInventoryFinalEconomyCompiler(deps: InventoryFinalEconomyDependencies):
    (target: ItemFieldRow, source: ItemFieldRow, sizeScale: number) => ItemFieldRow {
    const emit = (event: readonly unknown[]): void => { deps.trace?.(event); };
    const nodeItemId = (row: ItemFieldRow): string | null => { const id = readNodeItemId(row); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({ catalogRows: deps.catalogRows, nodeItemId,
        resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); } });
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const providers = function* (row: ItemFieldRow): Generator<ItemFieldRow> { emit(['providers']); yield* provider.providers(row); };
    const scalar = createLayerValueReader({ nodeItemId, inventoryContext, providers });
    const charges = createChargeReader(deps.chargePrefetch ?? null, (holder, item) => {
        emit(['charge-column', holder, item]); return deps.fetchChargeColumn(holder, item);
    });
    return (target, source, sizeScale) => compileInventoryFinalEconomy(target, source, sizeScale, {
        layerValue: scalar.read, offHandId: deps.offHandId,
        charges: (holder, item, fallback) => { emit(['charges', holder, item, fallback]); return charges(holder, item, fallback); },
    });
}
